import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import React, { Fragment, useCallback, useState } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import '../style.css';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { Test } from 'libs/end-to-end/src/tests-utils';
import { TestVideoStatus } from 'libs/end-to-end/src/tests-utils';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';

type TreatmentVideoModalProps = Omit<
  Parameters<typeof Dialog>[0],
  'onClose'
> & {
  tests: Test[];
  onClose: () => void;
};

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 12,
    slidesToSlide: 1,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 8,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 7,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 5,
    slidesToSlide: 1,
  },
};

export const TreatmentVideosModal = ({
  open,
  onClose,
  tests,
}: TreatmentVideoModalProps) => {
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);
  const handleTestVideoSelection = (test: Test) => {
    setSelectedTest(test);
  };

  const language = useLanguage();

  const isSelectedTestVideoAvailable =
    selectedTest?.videoStatus === TestVideoStatus.Values.available &&
    selectedTest.videoUrl !== undefined;
  const placeholderMessage = useCallback(
    () => getPlaceholderMessage(selectedTest, language),
    [selectedTest, language],
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        style={{ minWidth: '61rem' }}
        className="fixed inset-0 z-overlay overflow-y-auto font-sans"
        onClose={onClose}
      >
        <div className="min-h-screen text-center px-6">
          <Transition.Child>
            <Dialog.Overlay className="fixed inset-0 bg-gray-1000 bg-opacity-50" />
          </Transition.Child>
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              data-qa="treatmentSelectionModal"
              id="treatmentSelectionModal"
              className="inline-flex my-4 overflow-hidden text-left h-full align-middle transition-all transform bg-white shadow-12px rounded-lg w-full max-w-6xl flex-col relative"
            >
              <Dialog.Title
                as="h3"
                data-qa="treatmentVideoHeading"
                style={{
                  backgroundImage:
                    'radial-gradient(112.91% 137.83% at 115.91% 137.83%, #0A56A2 0%, #012C57 72.37%, #012C57 100%)',
                }}
                className="relative font-sans leading-6 font-normal text-xl p-6 text-white flex justify-between items-center text-wrap"
              >
                {language.testsLearnAboutFailedTests().toUpperCase()}
                <button
                  data-qa="videosCloseButton"
                  onClick={onClose}
                  className="inline-block p-1.5 hover:bg-blue-900 rounded-md bg-opacity-20 transition-colors"
                >
                  <IconCloseSvg svg={{ className: 'text-white w-4' }} />
                </button>
              </Dialog.Title>
              <div className="h-full ">
                <div className="h-full w-full p-4">
                  {isSelectedTestVideoAvailable ? (
                    <iframe
                      title="YouTube Video Player"
                      src={selectedTest.videoUrl}
                      className="w-full h-96 md:h-96 lg:h-[32rem]"
                      frameBorder="0"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    />
                  ) : (
                    <div
                      className="flex justify-center items-center h-96 md:h-96 lg:h-[32rem] bg-gray-200 bg-opacity-80"
                      id="scientificVideoContainer"
                    >
                      {placeholderMessage()}
                    </div>
                  )}
                </div>
                <div className="pb-4">
                  <Carousel
                    responsive={responsive}
                    slidesToSlide={1}
                    centerMode
                    containerClass="overflow-x-scroll justify-center "
                  >
                    {tests.map((test, index) => {
                      const isSelected =
                        selectedTest?.videoUrl !== undefined &&
                        selectedTest?.videoUrl === test.videoUrl;
                      const backgroundClass = isSelected
                        ? 'bg-blue-300 rounded'
                        : '';
                      const divClass = `m-1 p-4 text-center ${backgroundClass}`;
                      const thumbnailUrl =
                        test.videoThumbnailUrl !== undefined
                          ? test.videoThumbnailUrl
                          : 'images/icon-video-placeholder.svg';
                      return (
                        <div key={index} className={divClass}>
                          <img
                            src={thumbnailUrl}
                            alt={`video ${index + 1}`}
                            className=" cursor-pointer h-[66px]"
                            onClick={() => handleTestVideoSelection(test)}
                          />
                          <h5>Test {index + 1}</h5>
                        </div>
                      );
                    })}
                  </Carousel>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

const getPlaceholderMessage = (
  selectedTest: Test | null,
  language: Phrases,
) => {
  const isTestSelected = selectedTest !== null;
  const isSelectedTestVideoComingSoon =
    selectedTest?.videoStatus === TestVideoStatus.Values.comingSoon;
  const isSelectedTestVideoUnavailable =
    selectedTest?.videoStatus === TestVideoStatus.Values.unavailable ||
    selectedTest?.videoUrl === undefined;

  if (!isTestSelected) {
    return language.testsSelectAVideoBelowToStartWatching();
  } else if (isSelectedTestVideoComingSoon) {
    return language.testsVideoComingSoon();
  } else if (isSelectedTestVideoUnavailable) {
    return language.testsVideoUnavailable();
  } else {
    return '';
  }
};
